<template>
  <div class="gs-select" :class="{ 'is-open': open, 'is-disabled': disabled, 'has-value': hasValue }">
    <div class="gs-select-trigger" :class="{ 'is-disabled': disabled }">
      <input
        ref="field"
        v-model="text"
        class="gs-select-input"
        type="text"
        :disabled="disabled"
        :placeholder="placeholder"
        autocomplete="off"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.esc.prevent="cancel"
        @keydown.enter.prevent="pickFirst"
      >
      <button
        v-if="hasValue && !disabled"
        type="button"
        class="gs-select-clear"
        title="清除"
        aria-label="清除"
        @click.stop.prevent="clear"
      >×</button>
      <span class="gs-select-caret" aria-hidden="true" @click.stop="toggle"></span>
    </div>
    <teleport to="body">
      <div
        v-show="open && !disabled"
        ref="menu"
        class="gs-select-menu"
        :style="menuStyle"
        @mousedown.prevent
      >
        <button
          v-for="item in filteredOptions"
          :key="item.key"
          type="button"
          class="gs-select-option"
          :class="{ 'is-active': isActive(item) }"
          @click="pick(item)"
        >{{ item.label }}</button>
        <div v-if="!filteredOptions.length" class="gs-select-empty">暂无选项</div>
      </div>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'GsSelect',
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: ''
    },
    value: {
      type: [String, Number, Array],
      default: ''
    },
    options: {
      type: Array,
      default() {
        return []
      }
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      text: '',
      open: false,
      focused: false,
      editing: false,
      menuStyle: {},
      local: undefined
    }
  },
  computed: {
    current() {
      const fromProp = this.propValue()
      const propEmpty = Array.isArray(fromProp) ? fromProp.length === 0 : (fromProp === '' || fromProp == null)
      if (this.local !== undefined && propEmpty) return this.local
      return fromProp
    },
    hasValue() {
      if (Array.isArray(this.current)) return this.current.length > 0
      return this.current !== '' && this.current != null
    },
    normalizedOptions() {
      return (this.options || []).map((item, index) => ({
        value: item && item.value,
        key: String(item && item.value) + '-' + index,
        label: item && item.label != null ? String(item.label) : String(item && item.value)
      })).filter((item) => item.value !== '' && item.value != null)
    },
    filteredOptions() {
      if (!this.editing) return this.normalizedOptions
      const word = (this.text || '').trim().toLowerCase()
      if (!word) return this.normalizedOptions
      return this.normalizedOptions.filter((item) => item.label.toLowerCase().indexOf(word) >= 0)
    },
    displayLabel() {
      if (!this.hasValue) return ''
      if (Array.isArray(this.current)) {
        const labels = this.current.map((val) => {
          const hit = this.normalizedOptions.find((item) => String(item.value) === String(val))
          return hit ? hit.label : String(val)
        }).filter(Boolean)
        return labels.join('、')
      }
      const hit = this.normalizedOptions.find((item) => String(item.value) === String(this.current))
      return hit ? hit.label : String(this.current)
    }
  },
  watch: {
    disabled(val) {
      if (val) this.open = false
    },
    open(val) {
      if (val) this.$nextTick(this.syncMenu)
    },
    displayLabel: {
      immediate: true,
      handler(val) {
        if (this.focused) return
        this.text = val || ''
      }
    },
    text(val) {
      if (!this.focused) return
      const editing = (val || '') !== (this.displayLabel || '')
      this.editing = editing
      if (editing && !this.open) this.open = true
    },
    value() {
      this.local = undefined
    },
    modelValue() {
      this.local = undefined
    }
  },
  mounted() {
    this._onDoc = (event) => {
      if (this.$el && this.$el.contains(event.target)) return
      if (this.$refs.menu && this.$refs.menu.contains(event.target)) return
      this.open = false
    }
    this._onReposition = () => {
      if (this.open) this.syncMenu()
    }
    document.addEventListener('mousedown', this._onDoc)
    window.addEventListener('resize', this._onReposition)
    window.addEventListener('scroll', this._onReposition, true)
  },
  beforeUnmount() {
    this.unbindGlobal()
  },
  beforeDestroy() {
    this.unbindGlobal()
  },
  methods: {
    propValue() {
      if (Array.isArray(this.value)) return this.value
      if (this.value != null && this.value !== '') return this.value
      if (Array.isArray(this.modelValue)) return this.modelValue
      if (this.modelValue != null && this.modelValue !== '') return this.modelValue
      return this.multiple ? [] : ''
    },
    unbindGlobal() {
      if (this._blurTimer) window.clearTimeout(this._blurTimer)
      document.removeEventListener('mousedown', this._onDoc)
      window.removeEventListener('resize', this._onReposition)
      window.removeEventListener('scroll', this._onReposition, true)
    },
    syncMenu() {
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      const maxHeight = 260
      const spaceBelow = window.innerHeight - rect.bottom - 8
      const openUp = spaceBelow < 140 && rect.top > spaceBelow
      this.menuStyle = {
        position: 'fixed',
        left: `${Math.max(8, rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        zIndex: 4000,
        maxHeight: `${Math.min(maxHeight, openUp ? rect.top - 8 : spaceBelow)}px`,
        top: openUp ? 'auto' : `${rect.bottom + 4}px`,
        bottom: openUp ? `${window.innerHeight - rect.top + 4}px` : 'auto'
      }
    },
    toggle() {
      if (this.disabled) return
      if (this.open) {
        this.cancel()
        return
      }
      this.open = true
      this.$nextTick(() => {
        const input = this.$refs.field
        if (input && document.activeElement !== input) input.focus()
      })
    },
    cancel() {
      this.open = false
      this.editing = false
      this.text = this.displayLabel || ''
    },
    onFocus() {
      if (this.disabled) return
      if (this._blurTimer) {
        window.clearTimeout(this._blurTimer)
        this._blurTimer = 0
      }
      this.focused = true
      this.editing = false
      if (!this.text) this.text = this.displayLabel || ''
      if (!this.open) this.open = true
      this.$nextTick(() => {
        const input = this.$refs.field
        if (input && document.activeElement === input && input.value) input.select()
      })
    },
    onBlur() {
      const input = this.$refs.field
      this._blurTimer = window.setTimeout(() => {
        this._blurTimer = 0
        if (input && document.activeElement === input) return
        this.focused = false
        this.commitTyped()
      }, 0)
    },
    commitTyped() {
      const typed = (this.text || '').trim()
      this.editing = false
      this.open = false
      if (!this.multiple && typed) {
        const hit = this.normalizedOptions.find((item) => item.label === typed || String(item.value) === typed)
        if (hit) {
          this.pick(hit)
          return
        }
      }
      this.text = this.displayLabel || ''
    },
    pickFirst() {
      if (this.filteredOptions.length) this.pick(this.filteredOptions[0])
    },
    isActive(item) {
      if (Array.isArray(this.current)) {
        return this.current.some((val) => String(val) === String(item.value))
      }
      return String(this.current) === String(item.value)
    },
    emitValue(next) {
      this.$emit('input', next)
      this.$emit('change', next)
      this.$emit('update:modelValue', next)
      this.$emit('update:model-value', next)
    },
    pick(item) {
      if (this.multiple) {
        const list = Array.isArray(this.current) ? this.current.slice() : []
        const idx = list.findIndex((val) => String(val) === String(item.value))
        if (idx >= 0) list.splice(idx, 1)
        else list.push(item.value)
        this.local = list
        this.editing = false
        this.emitValue(list)
        this.text = this.displayLabel || ''
        return
      }
      this.local = item.value
      this.editing = false
      this.text = item.label
      this.emitValue(item.value)
      this.open = false
    },
    clear() {
      this.local = this.multiple ? [] : ''
      this.editing = false
      this.text = ''
      this.emitValue(this.multiple ? [] : '')
      this.open = false
    }
  }
}
</script>

<style lang="css" scoped>
.gs-select {
  position: relative;
  min-width: 0;
  width: 112px;
  flex: 1 1 96px;
}
.gs-select.wp100 {
  width: 100%;
  flex: 1 1 auto;
}
.gs-select.wp100 .gs-select-trigger {
  height: 36px;
}
.gs-select.wp100 .gs-select-text {
  font-size: 14px;
  font-weight: 400;
}
.gs-select-trigger {
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  border: 1px solid #D7DEE8;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  padding-right: 8px;
}
.gs-select.is-open .gs-select-trigger,
.gs-select-trigger:focus-within {
  border-color: #0165FF;
}
.gs-select-trigger.is-disabled {
  background: #F5F7FA;
  color: #C0C4CC;
}
.gs-select-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  padding: 0 4px 0 10px;
  background: #fff;
  color: #1f2937;
  color-scheme: light;
  caret-color: #1f2937;
  font-size: 14px;
  font-weight: 400;
}
.gs-select-input:focus {
  color: #1f2937;
  background: #fff;
  caret-color: #1f2937;
}
.gs-select-input::placeholder {
  color: #9aa3b2;
}
.gs-select-input[readonly] {
  cursor: pointer;
}
.gs-select-caret {
  cursor: pointer;
}
.gs-select-trigger.is-disabled .gs-select-main {
  cursor: not-allowed;
}
.gs-select-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
}
.gs-select-text.is-placeholder {
  color: #9aa3b2;
  font-weight: 400;
}
.gs-select-clear {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-right: 2px;
  line-height: 14px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #909399;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.gs-select-clear:hover {
  color: #fff;
  background: #C0C4CC;
}
.gs-select-caret {
  flex: 0 0 auto;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #909399;
}
.gs-select.is-open .gs-select-caret {
  transform: rotate(180deg);
}
</style>

<style lang="css">
.gs-select-menu {
  overflow: auto;
  background: #fff;
  border: 1px solid #E4E7ED;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}
.gs-select-search {
  display: block;
  width: calc(100% - 16px);
  margin: 8px;
  height: 30px;
  box-sizing: border-box;
  border: 1px solid #D7DEE8;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  background: #fff;
  color: #111824;
  color-scheme: light;
  caret-color: #111824;
  outline: none;
}
.gs-select-search:focus {
  border-color: #0165FF;
}
.gs-select-option {
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: 0;
  background: #fff;
  text-align: left;
  padding: 7px 10px;
  font-size: 13px;
  color: #1f2937;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gs-select-option:hover,
.gs-select-option.is-active {
  background: #EAF3FF;
  color: #0165FF;
}
.gs-select-empty {
  padding: 10px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
}
</style>
